import { motion } from "framer-motion";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import { Brain, BarChart3, Cloud, Database, Code2, ArrowUpRight } from "lucide-react";

const services = [
  {
    icon: Brain,
    num: "01",
    title: "AI Development",
    desc: "Custom artificial intelligence systems tailored to your business — from conversational agents to computer vision models.",
    features: ["Natural Language Processing", "Computer Vision", "Generative AI", "AI Agents"],
  },
  {
    icon: BarChart3,
    num: "02",
    title: "Machine Learning Solutions",
    desc: "End-to-end ML pipelines: data preprocessing, model training, evaluation, and production-grade deployment.",
    features: ["Predictive Analytics", "Recommendation Systems", "Anomaly Detection", "AutoML"],
  },
  {
    icon: Cloud,
    num: "03",
    title: "Cloud Integration",
    desc: "Seamless migration and integration with AWS, Azure, and GCP — optimised for performance, cost, and reliability.",
    features: ["Cloud Architecture", "DevOps & CI/CD", "Microservices", "Serverless"],
  },
  {
    icon: Database,
    num: "04",
    title: "Data Analytics",
    desc: "Transform raw data into actionable intelligence through analytics, visualisation, and real-time dashboards.",
    features: ["Business Intelligence", "Real-time Pipelines", "Data Warehousing", "ETL Systems"],
  },
  {
    icon: Code2,
    num: "05",
    title: "Custom Software Development",
    desc: "Full-stack development of enterprise-grade applications with clean architecture, robust APIs, and polished UIs.",
    features: ["Web Applications", "Mobile Apps", "API Design", "System Integration"],
  },
];

function FadeUp({ children, delay = 0, className = "" }) {
  const [ref, visible] = useScrollAnimation(0.1);
  return (
    <motion.div ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={visible ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}>
      {children}
    </motion.div>
  );
}

export default function Services() {
  return (
    <section id="services" className="relative py-24 bg-paper overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="flex items-start justify-between mb-10">
          <FadeUp>
            <span className="eyebrow">02 — Our services</span>
          </FadeUp>
        </div>

        <FadeUp delay={0.1} className="mb-14">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-[0.98] tracking-[-0.03em] text-ink max-w-2xl">
            End-to-end services, one accountable team.
          </h2>
        </FadeUp>

        {/* Numbered service list */}
        <div className="flex flex-col border-t-2 border-ink">
          {services.map(({ icon: Icon, num, title, desc, features }, i) => (
            <FadeUp key={title} delay={i * 0.06}>
              <div
                className="group grid md:grid-cols-12 gap-4 md:gap-8 py-8 items-start transition-colors duration-200 hover:bg-ink/[0.02]"
                style={{ borderBottom: "1px solid rgba(14,14,13,0.12)" }}
              >
                <div className="md:col-span-1 flex items-center gap-4 md:block">
                  <span className="text-sm font-mono text-ink-300">{num}</span>
                </div>

                <div className="md:col-span-1 flex items-center">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 bg-ink/5">
                    <Icon size={19} className="text-ink" />
                  </div>
                </div>

                <div className="md:col-span-4">
                  <h3 className="text-ink font-semibold text-lg mb-1.5">{title}</h3>
                  <p className="text-ink-500 text-sm leading-relaxed">{desc}</p>
                </div>

                <div className="md:col-span-5 flex flex-wrap content-start gap-2">
                  {features.map((f) => (
                    <span key={f}
                      className="text-xs px-2.5 py-1 rounded-full font-medium text-ink-500 bg-ink/4 border border-ink/10">
                      {f}
                    </span>
                  ))}
                </div>

                <div className="md:col-span-1 flex md:justify-end items-center">
                  <ArrowUpRight size={16}
                    className="text-ink-300 group-hover:text-ink transition-all duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}
